import { Box } from '@mui/material'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { Bandwidth, RequestBandwidth, ResponseBandwidth } from '../Bandwidth/Bandwidth'
import { ResolutionData, EntrySource, EntryDestination } from '../ResolutionData'
import { Entry } from '../../../EntryListItem/Entry'
import { ResourceType } from '../EntryData'
import { GenericDirectionIcon } from '../../../EntryListItem/icons/GenericDirectionIcon'

import styles from './FullResolutionData.module.sass'
import variables from '../../../../variables.module.scss'
import { NodeName } from '../NodeName'
import useSessionStorageState from 'use-session-storage-state'
import { SessionStorageKey } from '../../../../consts'
import { TrafficWebSocketStreamingOption } from '../../../Filters/WebsocketOptionsDropdown'
import { NavDivider } from '../../../Header/NavDivider'

import pluralize from 'pluralize';
import { ProcessName } from '../ProcessName'
import { Tooltip } from '../../../UI'
import { appDetailedStreamingEnabled } from '../../../../types/global'
import entryDetailsOpenAtom from '../../../../recoil/entryDetailsOpen'
import { useRecoilValue } from 'recoil'

const ResolutionContext = createContext({ getShownData: null, getGridItemColumnsNum: null })
export const useResolutionContext = () => useContext(ResolutionContext)

interface FullResolutionDataProps {
  entry: Entry
  showLongNames: boolean
  headingMode?: boolean
}

export const FullResolutionData: React.FC<FullResolutionDataProps> = ({
  entry,
  showLongNames,
  headingMode = false
}) => {
  const [wsOption] = useSessionStorageState(SessionStorageKey.TrafficWebSocketStreamingOption, {
    defaultValue: appDetailedStreamingEnabled() ?
      TrafficWebSocketStreamingOption.Detailed :
      TrafficWebSocketStreamingOption.FlowsOnly
  })

  const entryDetailsOpen = useRecoilValue(entryDetailsOpenAtom)

  const [node, setShowNode] = useState(false)
  const [namespace, setShowNamespace] = useState(false)
  const [service, setShowService] = useState(false)
  const [pod, setShowPod] = useState(true)

  useEffect(() => {
    const showAllResources = showLongNames || headingMode

    setShowNode(showAllResources)
    setShowNamespace(showAllResources)
    setShowService(showAllResources)
    setShowPod(true)
  }, [showLongNames, headingMode])

  return (
    <ResolutionContext.Provider
      value={{
        getShownData: (resourse: ResourceType) => {
          switch(resourse) {
          case 'name': return true
          case 'node': return node
          case 'namespace': return namespace
          case 'service': return service
          case 'pod': return pod
          }
        },
        getGridItemColumnsNum: (resourse: ResourceType) => {
          switch(resourse) {
          case 'name': return 11
          case 'node': return showLongNames || headingMode ? 3 : 0
          case 'namespace': return showLongNames || headingMode ? 2 : 0
          case 'service': return showLongNames || headingMode ? 2 : 0
          case 'pod': return showLongNames || headingMode ? 5 : 11
          }
        }
      }}
    >
      <Box
        className={styles.FullResolutionData}
        sx={ wsOption === TrafficWebSocketStreamingOption.FlowsOnly ? { width: 'auto !important' } : null }
      >
        <Box className={styles.ResolutionDataContainer}>
          <ResolutionData
            entry={entry}
            point={EntrySource}
            showLongNames={showLongNames}
          />
          {!headingMode && (entry?.requestSize !== 0 || entry?.responseSize !== 0) && (
            <Bandwidth type={RequestBandwidth} size={entry.requestSize} point={EntrySource} />
          )}
        </Box>
        <Box display='flex' ml={ headingMode ? '5px' : null}>
          <GenericDirectionIcon fill={entry.error ? variables.failureColor : entry.proto.backgroundColor} />
        </Box>
        <Box className={styles.ResolutionDataContainer}>
          <ResolutionData
            entry={entry}
            point={EntryDestination}
            showLongNames={showLongNames}
          />
          {!headingMode && (entry?.requestSize !== 0 || entry?.responseSize !== 0) && (
            <Bandwidth type={ResponseBandwidth} size={entry.responseSize} point={EntryDestination} />
          )}
        </Box>
        {(wsOption === TrafficWebSocketStreamingOption.FlowsOnly ||
         (wsOption === TrafficWebSocketStreamingOption.Detailed && !entryDetailsOpen)) && (
          <>
            {(entry?.src?.processName || entry?.dst?.processName) && (
              <Tooltip title='123'>
                <>
                  <Box ml='3px' mr='3px'>
                    <NavDivider backgroundColor={variables.lightSlateColor} />
                  </Box>
                  <span className='DataText' style={{ color: variables.fontColorAlt, fontWeight: 500 }}>
                    {pluralize(
                      'proc',
                      entry?.src?.processName && entry?.dst?.processName ? 2 : 1
                    )}:
                  </span>
                </>
              </Tooltip>
            )}
            {entry?.src?.processName && (
              <Box className={styles.ResolutionDataContainer}>
                <ProcessName
                  entry={entry}
                  point={EntrySource}
                />
              </Box>
            )}
            {entry?.src?.processName && entry?.dst?.processName && (
              <Box display='flex' ml={ headingMode ? '5px' : null}>
                <GenericDirectionIcon fill={entry.error ? variables.failureColor : entry.proto.backgroundColor} />
              </Box>
            )}
            {entry?.dst?.processName && (
              <Box className={styles.ResolutionDataContainer}>
                <ProcessName
                  entry={entry}
                  point={EntryDestination}
                />
              </Box>
            )}
            {(entry?.src?.pod?.spec?.nodeName || entry?.dst?.pod?.spec?.nodeName) && (
              <>
                <Box ml='3px' mr='3px'>
                  <NavDivider backgroundColor={variables.lightSlateColor} />
                </Box>
                <span className='DataText' style={{ color: variables.fontColorAlt, fontWeight: 500 }}>
                  {pluralize(
                    'node',
                    entry?.src?.pod?.spec?.nodeName && entry?.dst?.pod?.spec?.nodeName && (
                      entry?.src?.pod?.spec?.nodeName !== entry?.dst?.pod?.spec?.nodeName
                    ) ? 2 : 1
                  )}:
                </span>
              </>
            )}
            {entry?.src?.pod?.spec?.nodeName !== entry?.dst?.pod?.spec?.nodeName ? (
              <>
                {entry?.src?.pod?.spec?.nodeName && (
                  <Box className={styles.ResolutionDataContainer}>
                    <NodeName
                      entry={entry}
                      point={EntrySource}
                    />
                  </Box>
                )}
                {entry?.src?.pod?.spec?.nodeName && entry?.dst?.pod?.spec?.nodeName && (
                  <Box display='flex' ml={ headingMode ? '5px' : null}>
                    <GenericDirectionIcon fill={entry.error ? variables.failureColor : entry.proto.backgroundColor} />
                  </Box>
                )}
                {entry?.dst?.pod?.spec?.nodeName && (
                  <Box className={styles.ResolutionDataContainer}>
                    <NodeName
                      entry={entry}
                      point={EntryDestination}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box className={styles.ResolutionDataContainer}>
                <NodeName
                  entry={entry}
                  bothPoints
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </ResolutionContext.Provider>
  )
}
